import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserProfile } from '../services/api.js';
import { analyticsService } from '../services/analytics';

const Upgrade = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  
  // Load user data on mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const profile = await getUserProfile();
        setUserData(profile);
      } catch (err) {
        setError('Failed to load user data');
        console.error('Error loading user data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadUserData();
  }, []);

  // Theme effect to keep localStorage in sync
  useEffect(() => {
    localStorage.setItem('theme', theme);
  }, [theme]);

  const handleSelectPlan = async (tier) => {
    if (!userData) return;
    
    try {
      // Track upgrade event
      await analyticsService.trackTierUpgrade({
        userId: userData.id,
        fromTier: userData.tier,
        toTier: tier,
        method: 'payment_processor', // Payment processor integration required
        amount: tier === 'pro' ? 2 : tier === 'enterprise' ? 5 : 0,
        transactionId: `pending_${Date.now()}` // Will be updated with real transaction ID
      });

      // Also track with generic event
      await analyticsService.trackEvent('upgrade', {
        from_tier: userData.tier,
        to_tier: tier,
        plan_duration: 'monthly',
        amount: tier === 'pro' ? 2 : tier === 'enterprise' ? 5 : 0,
        currency: 'USD'
      });
    } catch (error) {
      console.warn('Failed to track upgrade:', error);
    }

    // In a real app, this would process payment
    try {
      // Note: Tier update API is not available in the backend yet
      // This would normally update the user's tier after payment processing
      setError('Tier upgrades are not available yet. Backend API endpoint needed.');
      console.log('Would upgrade to tier:', tier);
    } catch (error) {
      setError('Failed to update tier');
      console.error('Tier update error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/dashboard" className="text-blue-600 hover:underline">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Please log in to view upgrade options</p>
          <Link to="/signin" className="text-blue-600 hover:underline">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  const plans = [
    {
      tier: 'free',
      name: 'Free',
      price: '$0',
      period: 'forever',
      features: [
        'Unlimited links',
        '2 pre-built themes',
        '5-day basic analytics',
        'Email support',
        '"Hosted by Link Spaghetti ❤️" footer',
      ],
      notIncluded: [
        'Custom themes',
        'Advanced analytics',
        'SEO customization',
        'Remove branding',
      ],
    },
    {
      tier: 'pro',
      name: 'Pro',
      price: '$2',
      period: '/month',
      features: [
        'Unlimited links',
        '5 themes + limited color/font edits',
        '1-month analytics with device/geo/source',
        'Customize page title & description',
        'Hide or replace QuickLinks footer',
        '24-48 hour email support',
      ],
      notIncluded: [
        'Full CSS customization',
        'Lifetime analytics',
        'SEO audit reports',
        '24/7 chat support',
      ],
      popular: true,
    },
    {
      tier: 'max',
      name: 'Max',
      price: '$4',
      period: '/month',
      features: [
        'Everything in Pro',
        'Full CSS customization',
        'Lifetime analytics',
        'CSV export',
        'Scheduled email reports',
        'Remove all branding',
        'Custom meta tags',
        'Automated SEO audit',
        '24/7 chat support',
      ],
      notIncluded: [],
    },
  ];

  return (
    <div className={`min-h-screen ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-purple-50 via-white to-pink-50'
    }`}>
      {/* Header */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/dashboard" 
            className={`inline-flex items-center gap-2 mb-8 transition-colors ${
              theme === 'dark' 
                ? 'text-gray-400 hover:text-white' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </Link>

          <div className="text-center mb-16">
            <h1 className={`text-5xl font-bold mb-6 ${
              theme === 'dark' 
                ? 'bg-gradient-to-r from-[#2980b9] via-[#16a085] to-[#2980b9] bg-clip-text text-transparent'
                : 'bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent'
            }`}>
              All Features Unlocked! 🎉
            </h1>
            <p className={`text-xl max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Every feature is now completely free for all users - no payment required!
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              { icon: '🎨', title: 'All 7 Themes', desc: 'Access all premium themes including Neon, Pastel, and more' },
              { icon: '📊', title: 'Full Analytics', desc: 'Complete analytics with device tracking, geo data, and referrer info' },
              { icon: '🔗', title: 'Unlimited Links', desc: 'Create as many custom links as you need' },
              { icon: '⚙️', title: 'SEO Settings', desc: 'Full control over meta tags, OpenGraph, and search optimization' },
              { icon: '🎯', title: 'Custom Branding', desc: 'Customize or remove footer branding completely' },
              { icon: '📱', title: 'QR Codes', desc: 'Generate QR codes for your profile and links' },
            ].map((feature, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl transition-all ${
                  theme === 'dark'
                    ? 'bg-gray-800/50 border border-gray-700 hover:border-[#2980b9]'
                    : 'bg-white border border-gray-200 hover:border-purple-300 shadow-lg'
                }`}
              >
                <div className="text-4xl mb-3">{feature.icon}</div>
                <h3 className={`text-xl font-bold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {feature.title}
                </h3>
                <p className={`${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="mt-20 text-center">
            <div className={`inline-block p-8 rounded-2xl ${
              theme === 'dark' 
                ? 'bg-gradient-to-r from-[#2980b9]/20 to-[#16a085]/20 border border-[#2980b9]' 
                : 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 shadow-lg'
            }`}>
              <p className={`text-2xl font-bold mb-3 ${
                theme === 'dark' ? 'text-white' : 'text-gray-800'
              }`}>
                ✨ Everything is FREE - No Credit Card Required
              </p>
              <p className={`text-lg mb-6 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Start using all premium features right now!
              </p>
              <Link
                to="/dashboard"
                className={`inline-flex items-center gap-2 px-8 py-4 rounded-lg font-bold text-lg transition-all ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-[#2980b9] to-[#16a085] text-white hover:shadow-lg hover:shadow-[#2980b9]/50'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg hover:shadow-purple-500/50'
                }`}
              >
                Go to Dashboard →
              </Link>
              <p className={`text-sm mt-6 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Questions? Contact us at{' '}
                <a 
                  href="mailto:support@linkspaghetti.com" 
                  className={`font-medium transition-colors ${
                    theme === 'dark' 
                      ? 'text-[#16a085] hover:text-[#2980b9]' 
                      : 'text-purple-600 hover:text-pink-600'
                  }`}
                >
                  support@linkspaghetti.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upgrade; 